import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../layouts/DashboardLayout';
import BabyModal from '../components/BabyModal';
import RecipeCard from '../components/RecipeCard';
import RecipeModal from '../components/RecipeModal';
import { hydrateRecipeForModal } from '../utils/recipeModalData';
import NarrationSelector from '../components/NarrationSelector';
import QuickSearchReviewModal from '../components/QuickSearchReviewModal';
import HealthPlusActivationModal from '../components/HealthPlusActivationModal';
import PremiumPurchaseModal from '../components/PremiumPurchaseModal';
import HealthPlusMenuCard from '../components/HealthPlusMenuCard';
import HealthMedicalNotice from '../components/HealthMedicalNotice';
import { API_BASE_URL } from '../config';
import useBabyPrompt from '../hooks/useBabyPrompt';
import Swal from 'sweetalert2';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('authToken');
  if (token) return { Authorization: `Bearer ${token}`, ...extra };
  return { ...extra };
};

const QuickSearch = ({ isDarkMode, toggleDarkMode }) => {
  const [portion, setPortion] = useState(() => {
    const storedPortion = localStorage.getItem('userPortion');
    if (storedPortion) {
      const cleanPortion = storedPortion.replace(/[^\d]/g, '') || '2';
      localStorage.setItem('userPortion', cleanPortion);
      return cleanPortion;
    }
    return '2';
  });
  const [ingredients, setIngredients] = useState('');
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [recordStartTs, setRecordStartTs] = useState(0);
  const [dictating, setDictating] = useState(false);
  const [showDictatePrompt, setShowDictatePrompt] = useState(false);
  const timerIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [uploading, setUploading] = useState(false);
  const streamRef = useRef(null);
  const lastMicToggleRef = useRef(0);

  // Recipe Generation States
  const [recipes, setRecipes] = useState([]);
  const [loadingRecipes, setLoadingRecipes] = useState(false);
  const [recipeError, setRecipeError] = useState(null);
  const [searchCount, setSearchCount] = useState(0);
  const maxSearches = 3;

  // Baby & Profile Hook
  const { profile: babyProfile, showBabyModal, loading: babyLoading, submitBabyData, closeBabyModal } = useBabyPrompt();
  const [babyModalCompleted, setBabyModalCompleted] = useState(null);
  const [localBabyModalOpen, setLocalBabyModalOpen] = useState(false);
  const [userProfile, setUserProfile] = useState(null);

  const [selectedNarration, setSelectedNarration] = useState('guided');

  // Calculate initial skill level from profile
  const getInitialSkillLevel = (p) => {
    const raw = (p?.skillLevel || 'acemi').toLowerCase();
    if (raw.includes('mutfak')) return 'Usta Şef';
    if (raw.includes('orta')) return 'Deneyimli';
    return 'Acemi';
  };

  const [skillLevel, setSkillLevel] = useState('Acemi'); // Will update when profile loads
  const [loadingNarration, setLoadingNarration] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [pendingRecipe, setPendingRecipe] = useState(null);
  const [servePreference, setServePreference] = useState('mixed'); // NEW: Serve style filter

  // Review Modal States
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [parsedIngredients, setParsedIngredients] = useState([]);
  const [parsingIngredients, setParsingIngredients] = useState(false);
  const [showHealthPlusModal, setShowHealthPlusModal] = useState(false);
  const [showHealthPlusPaymentModal, setShowHealthPlusPaymentModal] = useState(false);
  const [healthPlusActive, setHealthPlusActive] = useState(false);
  const [healthPlusEntitled, setHealthPlusEntitled] = useState(false);
  const [healthPlusBusy, setHealthPlusBusy] = useState(false);

  useEffect(() => {
    if (babyProfile) {
      setUserProfile(babyProfile);
      setSkillLevel(getInitialSkillLevel(babyProfile));
    }

    if (!babyLoading) {
      const hasBabyInfo = Boolean(babyProfile?.babyBirthDate || babyProfile?.babyAgeGroup);
      const needsPrompt = showBabyModal && !hasBabyInfo;
      setBabyModalCompleted(needsPrompt ? false : true);

      if (babyProfile) {
        if (babyProfile.servingSize) {
          const numericValue = String(babyProfile.servingSize).replace(/[^\d]/g, '') || '2';
          setPortion(numericValue);
          localStorage.setItem('userPortion', numericValue);
        } else if (!localStorage.getItem('userPortion')) {
          let sv = null;
          if (babyProfile.intakeSurvey && babyProfile.intakeSurvey.answers) {
            const answers = babyProfile.intakeSurvey.answers;
            sv = answers.serving || answers.q3 || answers.servingSize;
          }
          sv = sv || (babyProfile.intakeSurvey && babyProfile.intakeSurvey.servingSize);
          if (sv) {
            const numericValue = String(sv).replace(/[^\d]/g, '') || '2';
            setPortion(numericValue);
            localStorage.setItem('userPortion', numericValue);
          }
        }
      }
    }
  }, [babyProfile, babyLoading, showBabyModal]);

  const fetchHealthPlusState = async () => {
    try {
      const [profileRes, stateRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/profile`, {
          credentials: 'include',
          headers: getAuthHeaders()
        }),
        fetch(`${API_BASE_URL}/api/health/activation-state`, {
          credentials: 'include',
          headers: getAuthHeaders()
        })
      ]);
      const profileData = await profileRes.json().catch(() => ({}));
      const stateData = await stateRes.json().catch(() => ({}));
      const entitled = !!profileData?.entitlements?.modules?.global?.granted
        && !profileData?.entitlements?.lockedByTime;
      setHealthPlusEntitled(entitled);
      setHealthPlusActive(entitled && !!stateData?.enabled);
    } catch (error) {
      console.warn('health+ state fetch warning', error);
      setHealthPlusEntitled(false);
      setHealthPlusActive(false);
    }
  };

  useEffect(() => {
    fetchHealthPlusState();
  }, []);

  const setHealthPlusActivationState = async (enabled) => {
    try {
      setHealthPlusBusy(true);
      const res = await fetch(`${API_BASE_URL}/api/health/activation-state`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ enabled: !!enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'health_activation_state_update_failed');
      setHealthPlusActive(!!data.enabled);
    } catch (error) {
      console.error('health+ toggle failed', error);
      Swal.fire('Hata', 'Sağlık+ durumu güncellenemedi.', 'error');
    } finally {
      setHealthPlusBusy(false);
    }
  };

  const handleHealthPlusCardClick = async () => {
    if (healthPlusBusy) return;
    if (healthPlusActive) {
      await setHealthPlusActivationState(false);
      return;
    }
    if (healthPlusEntitled) {
      await setHealthPlusActivationState(true);
      return;
    }
    setShowHealthPlusModal(true);
  };

  const handleHealthPlusProceedToPayment = () => {
    setShowHealthPlusModal(false);
    setShowHealthPlusPaymentModal(true);
  };

  const handleRecipeCardClick = async (recipe) => {
    // Stage 1: Directly show modal with already populated steps
    setSelectedRecipe(await hydrateRecipeForModal(recipe));
    setShowRecipeModal(true);
  };

  const handleCloseRecipeModal = () => {
    setShowRecipeModal(false);
    setSelectedRecipe(null);
  };

  // Mic/STT Handlers
  const handleMicToggle = async (e) => {
    if (e && e.type === 'touchstart') {
      try { e.preventDefault(); } catch (er) { }
    }
    try {
      const now = Date.now();
      if (lastMicToggleRef.current && (now - lastMicToggleRef.current) < 700) return;
      lastMicToggleRef.current = now;
    } catch (er) { }
    if (uploading) return;
    const isMobile = typeof navigator !== 'undefined' && (/Mobi|Android|iPhone|iPad/.test(navigator.userAgent) || (navigator.maxTouchPoints && navigator.maxTouchPoints > 0));
    if (isMobile) {
      if (!dictating) {
        setShowDictatePrompt(true);
        return;
      } else {
        textareaRef.current && textareaRef.current.blur();
        setDictating(false);
        return;
      }
    }
    if (navigator.mediaDevices && window.MediaRecorder) {
      if (recording) {
        setRecording(false);
        setUploading(true);
        mediaRecorderRef.current && mediaRecorderRef.current.stop();
      } else {
        try {
          if (typeof window !== 'undefined' && window.location && window.location.protocol !== 'https:' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
            return alert('Mikrofon erişimi için HTTPS gerekiyor.');
          }
          setRecording(true);
          const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
          streamRef.current = stream;
          let mimeType = '';
          const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/wav'];
          for (const c of candidates) {
            if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(c)) { mimeType = c; break; }
          }
          const mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
          chunksRef.current = [];
          mr.ondataavailable = e => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
          mr.onstop = async () => {
            const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
            const ext = blob.type.includes('ogg') ? 'ogg' : (blob.type.includes('wav') ? 'wav' : 'webm');
            const file = new File([blob], `rec.${ext}`, { type: blob.type });
            try {
              const fd = new FormData();
              fd.append('audio', file);
              const res = await fetch(`${API_BASE_URL}/api/stt`, {
                method: 'POST',
                body: fd,
                credentials: 'include',
                headers: getAuthHeaders()
              });
              if (res.ok) {
                const j = await res.json();
                const t = j.transcript || j.text || '';
                if (t) setIngredients(prev => (prev ? prev + ', ' + t : t));
              }
            } catch (err) {
              console.error('STT Error:', err);
            } finally {
              streamRef.current && streamRef.current.getTracks().forEach(t => t.stop());
              setUploading(false);
              clearInterval(timerIntervalRef.current);
              setRecordStartTs(0);
            }
          };
          mediaRecorderRef.current = mr;
          mr.start();
          setRecordStartTs(Date.now());
          timerIntervalRef.current = setInterval(() => setRecordStartTs(ts => ts), 1000);
        } catch (err) {
          setRecording(false);
          alert('Mikrofon erişimi başarısız');
        }
      }
    }
  };

  const confirmDictation = () => {
    setShowDictatePrompt(false);
    setDictating(true);
    setTimeout(() => textareaRef.current && textareaRef.current.focus(), 50);
  };

  const executeFinalSearch = async (finalIngredients) => {
    setLoadingRecipes(true);
    setRecipeError(null);
    setRecipes([]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/quick-search/suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          query: ingredients.trim(), // Keep original for history/logs if needed
          structuredIngredients: finalIngredients, // Send the confirmed/refined list
          servingSize: parseInt(portion) || 2,
          skillLevel: skillLevel,
          servePreference: servePreference
        })
      });

      if (response.ok) {
        const data = await response.json();
        const recipesToSet = data.suggestions || data.data?.recipes || [];
        setRecipes(recipesToSet);
        setSearchCount(prev => prev + 1);
        setTimeout(() => document.getElementById('recipe-results')?.scrollIntoView({ behavior: 'smooth' }), 300);
      } else {
        const errorData = await response.json();
        setRecipeError(errorData.message || 'Tarif aranırken bir hata oluştu');
      }
    } catch (error) {
      setRecipeError('Bağlantı hatası.');
    } finally {
      setLoadingRecipes(false);
    }
  };

  const performRecipeSearch = async () => {
    setLoadingRecipes(true);
    setRecipeError(null);
    setRecipes([]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/quick-search/suggest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({
          query: ingredients.trim(),
          servingSize: parseInt(portion) || 2,
          skillLevel: skillLevel,
          servePreference: servePreference  // NEW: Send serve preference
        })
      });

      if (response.ok) {
        const data = await response.json();
        const recipesToSet = data.suggestions || data.data?.recipes || [];
        setRecipes(recipesToSet);
        setSearchCount(prev => prev + 1);
        setTimeout(() => document.getElementById('recipe-results')?.scrollIntoView({ behavior: 'smooth' }), 300);
      } else {
        const errorData = await response.json();
        setRecipeError(errorData.message || 'Tarif aranırken bir hata oluştu');
      }
    } catch (error) {
      setRecipeError('Bağlantı hatası.');
    } finally {
      setLoadingRecipes(false);
    }
  };

  const handleRecipeSearch = async () => {
    if (!ingredients.trim()) return setRecipeError('Lütfen malzemeleri girin');

    // 1. Alerjen Kontrolü - Yeni Güvenlik Katmanı
    const allergies = userProfile?.alerji_listesi || [];
    if (allergies.length > 0) {
      const lowerIngredients = ingredients.toLowerCase();
      const foundAllergies = allergies.filter(a => lowerIngredients.includes(a.toLowerCase()));

      if (foundAllergies.length > 0) {
        const result = await Swal.fire({
          title: 'Dikkat Şefim! ⚠️',
          text: `Alerjiniz olduğunu belirttiğiniz bir ürün (${foundAllergies.join(', ')}) için arama yapıyorsunuz! Yine de devam etmek istiyor musunuz?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#ff0000',
          cancelButtonColor: '#3085d6',
          confirmButtonText: 'Evet, Devam Et',
          cancelButtonText: 'Hayır, Durdur',
          reverseButtons: true
        });

        if (!result.isConfirmed) {
          return; // Aramayı durdur
        }
      }
    }

    if (searchCount >= maxSearches) return setRecipeError(`Günlük ${maxSearches} hakkınız doldu`);
    if (!babyLoading && babyModalCompleted === false) {
      setLocalBabyModalOpen(true);
      return;
    }

    // New Flow: First Parse, then Review Modal
    setParsingIngredients(true);
    setRecipeError(null);

    try {
      const resp = await fetch(`${API_BASE_URL}/api/quick-search/parse`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ query: ingredients.trim() })
      });

      if (resp.ok) {
        const data = await resp.json();
        setParsedIngredients(data.ingredients || []);
        setShowReviewModal(true);
      } else {
        const errData = await resp.json();
        setRecipeError(errData.message || 'Malzemeler ayrıştırılamadı.');
      }
    } catch (err) {
      setRecipeError('Bağlantı hatası.');
    } finally {
      setParsingIngredients(false);
    }
  };

  const handleBabyModalSubmit = async (babyData) => {
    await submitBabyData(babyData);
    setBabyModalCompleted(true);
    setLocalBabyModalOpen(false);
    setUserProfile(prev => ({ ...prev, ...babyData }));
    setTimeout(performRecipeSearch, 100);
  };

  return (
    <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
      <div className="flex flex-col items-center justify-center w-full relative pt-10 max-w-7xl mx-auto gap-10 animate-fade-in my-auto">
        <div className="text-center space-y-4">
          <h3 className="text-text-main text-5xl sm:text-3xl font-black leading-tight tracking-[-0.033em] dark:text-white">Bugün ne pişirelim?</h3>
          <p className="text-text-muted text-xl font-normal leading-relaxed max-w-2xl mx-auto dark:text-gray-400">Elinizde ki malzemeleri girin, o  malzemeler ile yapılabilecek en uyumlu yemekler listelensin</p>
        </div>

        <HealthPlusMenuCard
          isActive={healthPlusActive}
          inactiveButtonLabel={healthPlusEntitled ? 'Sağlık+ başlat' : 'Sağlık+ aktivasyonu'}
          onClick={handleHealthPlusCardClick}
          className="w-full max-w-[75rem] mx-auto"
        />
        <HealthMedicalNotice
          enabled={healthPlusActive}
          targetMemberKey="household"
          className="w-full max-w-[75rem] mx-auto -mt-6"
        />
        <div className="w-full max-w-[75rem] mx-auto flex flex-col gap-8 bg-card-bg dark:bg-[#09090b] border border-border-light dark:border-white/5 p-10 sm:p-14 rounded-3xl shadow-xl shadow-slate-200/60 dark:shadow-none">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="flex flex-col gap-3">
              <label className="text-text-main text-sm font-bold uppercase tracking-wider pl-1 dark:text-white" htmlFor="portion-select">Porsiyon Sayısı</label>
              <div className="relative">
                <input id="portion-select" type="number" min={1} value={portion} onChange={e => { setPortion(e.target.value); localStorage.setItem('userPortion', e.target.value); }} className="w-full rounded-2xl bg-input-bg dark:bg-[#0b0b0b] border border-border-light dark:border-gray-800 p-6 text-lg font-medium shadow-sm pr-12" />
                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-slate-400 font-medium tracking-tight">Kişilik</span>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-text-main text-sm font-bold uppercase tracking-wider pl-1 dark:text-white">Mutfakla Aranız Nasıl?</label>
              <div className="flex bg-input-bg dark:bg-[#0b0b0b] p-2 rounded-2xl border border-border-light dark:border-gray-800 shadow-sm overflow-hidden h-full">
                {['Acemi', 'Deneyimli', 'Usta Şef'].map((lvl) => (
                  <button
                    key={lvl}
                    onClick={() => setSkillLevel(lvl)}
                    className={`flex-1 flex items-center justify-center py-2 px-3 rounded-xl text-sm font-bold transition-all duration-300 ${skillLevel === lvl ? 'bg-primary text-white shadow-lg scale-105' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-white/5'}`}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <label className="text-text-main text-sm font-bold uppercase tracking-wider pl-1 dark:text-white" htmlFor="ingredients-input">Malzemeler</label>
            <div className="relative group">
              <textarea ref={textareaRef} id="ingredients-input" value={ingredients} onChange={e => setIngredients(e.target.value)} placeholder="Elinizdeki malzemeleri yazın ya da sesle doldurun..." className="w-full min-h-[200px] resize-none rounded-2xl bg-input-bg dark:bg-[#0b0b0b] border border-border-light dark:border-gray-800 p-6 text-lg font-normal shadow-sm" />
              <div className="absolute bottom-4 right-4 bg-white/50 dark:bg-white/5 backdrop-blur-sm rounded-lg p-1 flex items-center gap-2">
                <button onClick={handleMicToggle} className={`p-2 rounded-md ${recording ? 'bg-red-600 text-white' : 'bg-white/0 text-slate-600'}`}>
                  {uploading ? <span className="material-symbols-outlined animate-spin">autorenew</span> : (recording ? <span className="material-symbols-outlined">stop</span> : <span className="material-symbols-outlined">mic</span>)}
                </button>
              </div>
            </div>
          </div>

          {/* Sunum Şekli Seçimi - NEW */}
          <div className="flex flex-col gap-3">
            <label className="text-text-main text-sm font-bold uppercase tracking-wider pl-1 dark:text-white">
              Ne Hazırlayalım?
            </label>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { value: 'ana_yemek', icon: '🍽️', label: 'Ana Yemek' },
                { value: 'meze', icon: '🥗', label: 'Meze' },
                { value: 'yan_yemek', icon: '🥪', label: 'Hafif Öğün' },
                { value: 'tatli', icon: '🍰', label: 'Tatlı' },
                { value: 'mixed', icon: '✨', label: 'Karışık' }
              ].map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setServePreference(option.value)}
                  className={`px-4 py-4 rounded-xl font-medium transition-all ${servePreference === option.value
                    ? 'bg-primary text-white shadow-lg scale-105'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                    }`}
                >
                  <span className="text-2xl block mb-1">{option.icon}</span>
                  <span className="text-xs">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-4 border-t border-dashed border-border-light mt-2">
            <div className="flex items-center gap-3 bg-slate-50 dark:bg-[#0b0b0b] px-5 py-4 rounded-xl border border-border-light dark:border-white/5 w-full sm:w-auto">
              <p className="text-slate-600 dark:text-gray-300 text-sm font-medium">{maxSearches} arama hakkınızdan <span className="text-primary font-bold">{maxSearches - searchCount}</span> tane kaldı.</p>
            </div>
            <button
              onClick={handleRecipeSearch}
              disabled={loadingRecipes || parsingIngredients || !ingredients.trim() || (searchCount >= maxSearches)}
              className="w-full sm:w-auto min-w-[220px] h-16 bg-primary text-white text-xl font-bold rounded-2xl transition-all shadow-lg active:scale-95 disabled:bg-gray-400"
            >
              {parsingIngredients ? 'Hazırlanıyor...' : (loadingRecipes ? 'Aranıyor...' : 'Tarif Bul')}
            </button>
          </div>
        </div>

        {
          recipeError && (
            <div className="w-full max-w-4xl mt-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {recipeError}
            </div>
          )
        }

        {
          recipes.length > 0 && (
            <div id="recipe-results" className="w-full max-w-6xl mt-8 bg-white dark:bg-[#09090b] border border-border-light dark:border-white/5 rounded-3xl p-8 shadow-xl">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {recipes.map((recipe, index) => (
                  <RecipeCard key={recipe.id || index} recipe={recipe} onCardClick={handleRecipeCardClick} showBabyInfo={true} userProfile={userProfile} userPortion={parseInt(portion) || 1} isModern={true} />
                ))}
              </div>
            </div>
          )
        }
      </div>

      {
        showDictatePrompt && (
          <div className="fixed left-1/2 top-20 z-50 transform -translate-x-1/2 w-11/12 max-w-md bg-white dark:bg-[#0b0b0b] border border-border-light dark:border-white/5 rounded-lg p-6 shadow-lg flex items-center gap-4">
            <p className="text-sm">Klavye diktesini kullanmak için konuşun.</p>
            <button onClick={confirmDictation} className="bg-primary text-white px-4 py-2 rounded-lg">Tamam</button>
          </div>
        )
      }

      <BabyModal isOpen={localBabyModalOpen} onClose={() => { setLocalBabyModalOpen(false); closeBabyModal(); }} onSubmit={handleBabyModalSubmit} />

      {
        loadingNarration && (
          <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/80 backdrop-blur-xl animate-fade-in px-6 text-center">
            <div className="relative mb-12">
              <div className="w-32 h-32 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="material-symbols-outlined text-4xl text-primary animate-pulse">restaurant</span>
              </div>
            </div>
            <h2 className="text-3xl font-black text-white mb-4 tracking-tighter">Şef Hazırlanıyor...</h2>
            <p className="text-xl text-white/70 max-w-md font-medium leading-relaxed">
              <span className="text-primary font-bold">"{pendingRecipe?.title}"</span> tarifi {skillLevel.toLowerCase()} seviyesine uygun şekilde sizin için özelleştiriliyor.
            </p>
            <div className="mt-12 flex gap-3">
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
              <div className="w-2 h-2 bg-primary rounded-full animate-bounce"></div>
            </div>
          </div>
        )
      }

      <RecipeModal
        onPairingClick={handleRecipeCardClick}
        isOpen={showRecipeModal}
        onClose={handleCloseRecipeModal}
        recipe={selectedRecipe}
        userProfile={userProfile}
        showBabyInfo={true}
        userPortion={parseInt(portion) || 1}
        selectedNarration={selectedNarration}
        skillLevel={skillLevel}
      />

      <QuickSearchReviewModal
        isOpen={showReviewModal}
        ingredients={parsedIngredients}
        onClose={() => setShowReviewModal(false)}
        onConfirm={executeFinalSearch}
      />

      <HealthPlusActivationModal
        isOpen={showHealthPlusModal}
        onClose={() => setShowHealthPlusModal(false)}
        onProceedToPayment={handleHealthPlusProceedToPayment}
      />

      <PremiumPurchaseModal
        isOpen={showHealthPlusPaymentModal}
        onClose={async (reason) => {
          if (reason === 'open') {
            setShowHealthPlusPaymentModal(true);
            return;
          }
          setShowHealthPlusPaymentModal(false);
          if (reason === 'verified') {
            setHealthPlusEntitled(true);
            await setHealthPlusActivationState(true);
            return;
          }
          fetchHealthPlusState();
        }}
        preferredModuleKey="global"
        preferredModuleTitle="Sağlık+ Premium"
      />
    </DashboardLayout>
  );
};

export default QuickSearch;
